import { normalizeCurrency } from './cookie-trackers/purchase/currency';
import { googleTagCreateYourAccountScript } from './cookie-trackers/purchase/google-tag-create-your-account';
import { metaPixelStartTrialScript } from './cookie-trackers/purchase/meta-pixel-start-trial';
import { metaPixelTrackOrderScript } from './cookie-trackers/purchase/meta-pixel-track-order';
import podscribePixelPurchaseStr from './cookie-trackers/purchase/podscribe-pixel-purchase.js?raw';
import quoraPixelPurchaseStr from './cookie-trackers/purchase/quora-pixel-purchase.js?raw';
import { tiktokPlaceOrderScript } from './cookie-trackers/purchase/tiktok-place-order';
import twitterTrackingPurchaseStr from './cookie-trackers/purchase/twitter-tracking-purchase.js?raw';
import { type CookieTrackers, strToInlineScript } from './seo';
import { shouldDisableTrackers } from './tracker-suppression';

// Both signals are resolved by the /post-purchase route loader, which
// calls `Billing.Confirm` server-side during SSR and
// reads the real subscription state from Stripe.
//
//   - `amountUsd` — null unless `subscription.status === 'active'` AND
//     the line item has a positive `unit_amount`. Trialing subs have
//     amountUsd null because no money has changed hands yet (even
//     though `unit_amount` is set to the eventual charge).
//   - `currency` — the ISO 4217 code Stripe billed in, read off the
//     same price. Unlike `amountUsd` it is NOT gated on subscription
//     status, so a trialing sub still carries one. Null only when the
//     price has no currency or the call failed; `normalizeCurrency()`
//     falls back to USD there.
//   - `trialStarted` — true iff `subscription.status === 'trialing'`.
//     Sourced from Stripe, NOT from the frontend
//     `useFreeTrialAvailable()` config flag.
//
// The two signals are mutually exclusive in practice (the same
// subscription can't be both `active` and `trialing` simultaneously),
// but we treat them independently here so the firing logic doesn't
// rely on that invariant.
//
// See docs/projects/active/audit-cookie-trackers.md Phase R2 for the
// full architecture rationale (why SSR loader vs URL params vs CAPI).
type CookieTrackersPurchaseArgs = {
  amountUsd: number | null;
  currency: string | null;
  trialStarted: boolean;
};

// Stripe `confirmPayment()` redirects the entire browser to
// `/post-purchase?redirect_status=...` after redirect-based payment
// methods (3D Secure, wallets that pop out, etc.) — including when the
// payment FAILED. The route loader already returns null/false signals
// for `redirect_status === 'failed'`, so a failed redirect won't even
// reach this function via the head() path. The runtime gate below is
// belt-and-suspenders for the "loader returned a positive signal but
// Stripe also re-stamped redirect_status=failed at the last second"
// edge case — bail out before tracking if the URL says the payment
// failed, regardless of what the loader saw.
function gatePurchaseScript(body: string): string {
  return `(()=>{const r=new URLSearchParams(window.location.search).get('redirect_status');if(r==='failed')return;${body}})();`;
}

export function cookieTrackersPurchase({
  amountUsd,
  currency,
  trialStarted,
}: CookieTrackersPurchaseArgs): CookieTrackers {
  if (shouldDisableTrackers()) return [];

  const currencyCode = normalizeCurrency(currency);

  // Trial-signup branch. Currently only Meta has a wired trial event —
  // see `meta-pixel-start-trial.ts` for the per-platform coverage gap.
  // When trials are re-enabled and per-platform trial events are
  // verified, add additional scripts here (TikTok, Reddit, etc.).
  if (trialStarted) {
    return [
      strToInlineScript(
        gatePurchaseScript(metaPixelStartTrialScript(currencyCode)),
      ),
    ];
  }

  // Purchase branch. No trustworthy revenue → skip every conversion
  // pixel. Site-wide pixels (Meta PageView, Reddit PageVisit, etc.)
  // still fire from `cookieTrackersSiteWide()`; only the
  // purchase-stage conversion events are gated on a real amount.
  // Zero / negative / non-finite values are rejected one layer up by
  // the response schema (`Schema.Number.pipe(finite, positive)`), so
  // anything non-null here is real money.
  if (amountUsd === null) return [];

  return [
    strToInlineScript(
      gatePurchaseScript(tiktokPlaceOrderScript(amountUsd, currencyCode)),
    ),
    strToInlineScript(
      gatePurchaseScript(metaPixelTrackOrderScript(amountUsd, currencyCode)),
    ),
    strToInlineScript(
      gatePurchaseScript(
        googleTagCreateYourAccountScript(amountUsd, currencyCode),
      ),
    ),
    strToInlineScript(gatePurchaseScript(quoraPixelPurchaseStr)),
    strToInlineScript(gatePurchaseScript(podscribePixelPurchaseStr)),
    strToInlineScript(gatePurchaseScript(twitterTrackingPurchaseStr)),
  ];
}
